'use client';

import { useCallback, useEffect, useRef, useState } from 'react';

interface UsePanelResizeOptions {
    minWidth: number;
    maxWidth: number;
    defaultWidth: number;
    storageKey: string;
}

interface UsePanelResizeReturn {
    width: number;
    handlePointerDown: (e: React.PointerEvent) => void;
    isDragging: boolean;
}

function readStoredWidth(key: string, fallback: number, min: number, max: number): number {
    if (typeof window === 'undefined') return fallback;
    try {
        const raw = localStorage.getItem(key);
        if (raw === null) return fallback;
        const parsed = Number(raw);
        if (Number.isFinite(parsed)) return Math.min(max, Math.max(min, parsed));
    } catch {
        /* localStorage may be blocked */
    }
    return fallback;
}

/**
 * Drag-to-resize for a right-anchored panel.
 *
 * Uses pointer capture so the drag cannot be interrupted by iframes or
 * other elements that would normally swallow mouse events.
 */
export function usePanelResize({
    minWidth,
    maxWidth,
    defaultWidth,
    storageKey,
}: UsePanelResizeOptions): UsePanelResizeReturn {
    const [width, setWidth] = useState(() =>
        readStoredWidth(storageKey, defaultWidth, minWidth, maxWidth),
    );
    const [isDragging, setIsDragging] = useState(false);

    const widthRef = useRef(width);
    widthRef.current = width;

    const minRef = useRef(minWidth);
    const maxRef = useRef(maxWidth);
    minRef.current = minWidth;
    maxRef.current = maxWidth;

    const storageKeyRef = useRef(storageKey);
    storageKeyRef.current = storageKey;

    const rafRef = useRef(0);

    useEffect(() => {
        return () => cancelAnimationFrame(rafRef.current);
    }, []);

    const persist = useCallback((w: number) => {
        try {
            localStorage.setItem(storageKeyRef.current, String(w));
        } catch {
            /* noop */
        }
    }, []);

    const handlePointerDown = useCallback(
        (e: React.PointerEvent) => {
            e.preventDefault();
            const handle = e.currentTarget as HTMLElement;
            handle.setPointerCapture(e.pointerId);

            const startX = e.clientX;
            const startWidth = widthRef.current;

            setIsDragging(true);
            document.body.style.cursor = 'col-resize';
            document.body.style.userSelect = 'none';

            const onPointerMove = (mv: PointerEvent) => {
                cancelAnimationFrame(rafRef.current);
                rafRef.current = requestAnimationFrame(() => {
                    const delta = startX - mv.clientX;
                    const next = Math.min(
                        maxRef.current,
                        Math.max(minRef.current, startWidth + delta),
                    );
                    setWidth(next);
                });
            };

            const cleanup = () => {
                cancelAnimationFrame(rafRef.current);
                setIsDragging(false);
                document.body.style.cursor = '';
                document.body.style.userSelect = '';
                persist(widthRef.current);
                handle.removeEventListener('pointermove', onPointerMove);
                handle.removeEventListener('pointerup', cleanup);
                handle.removeEventListener('lostpointercapture', cleanup);
            };

            handle.addEventListener('pointermove', onPointerMove);
            handle.addEventListener('pointerup', cleanup);
            handle.addEventListener('lostpointercapture', cleanup);
        },
        [persist],
    );

    return { width, handlePointerDown, isDragging };
}
